/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import {
  BodyText,
  Button,
  Div,
  Flex,
  GrowlNotification,
  GrowlNotificationSupportedVariant,
  Gutter,
  OptionListOption,
  Step,
  Steps,
  Tag,
  Title,
} from '@gaiads/telia-react-component-library';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios, { AxiosResponse } from 'axios';
import { isEqual } from 'lodash';
import {
  Address,
  AdminOrganizationRequest,
  BadRequestErrorErrorEnum,
  EndOfLeaseOptions,
  NotFoundErrorStatusEnum,
  OrganizationPolicies,
  OrganizationResponse,
  OrganizationWithUserGroupsResponse,
  Person,
  UserGroupResponse,
  UserGroupWithUsersResponse,
  UserRole,
} from '@cdaas/api-clients/dist/asset-admin-openapi';
import { SalesChannel as SalesChannelTsInterface } from '@cdaas/api-clients/dist/product-service-openapi';
import { Channel } from '@cdaas/api-clients/dist/order-admin-openapi';

import { assetAdminOrganizationClient, assetAdminUserClient, assetAdminUserGroupClient } from '../../api-client/asset-admin-client';
import { productServiceAdminSalesChannelsClient } from '../../api-client/product-service-client';
import { orderAdminChannelClient } from '../../api-client/order-admin-client';

import CompanyInformation from './CompanyInformation/CompanyInformation';
import BillingInformation, { BillingAddressDraft, BillingContactPersonDraft } from './BillingInformation/BillingInformation';
import DeviceEnrollment from './DeviceEnrollment/DeviceEnrollment';
import SalesChannel from './SalesChannel/SalesChannel';
import Administrators from './Administrators/Administrators';
import EndOfLease from './EndOfLease/EndOfLease';
import DeviceLimit from './DeviceLimit/DeviceLimit';
import CompanyDevicePolicy from './CompanyDevicePolicy/CompanyDevicePolicy';
import ParentOrganization from './ParentOrganization/ParentOrganization';
import { Loader } from '../App/Loader';
import Message from '../App/Message';
import RegionSelection from './RegionSelection/RegionSelection';

import {
  Errors,
  setCustomerBusinessIdAlreadyExists,
  setCustomerBusinessIdNorwayError,
  setParentOrganizationError,
  validateCustomerOrganization,
} from './utils';
import {
  addKey,
  compareIds,
  getUserGroupRequestToCreateNewUserGroup,
  getUserRequest,
  getUserResponseFromUserListUser,
  getUserUpdateRequest,
  mapAssetBackendErrorCodeToLocalizedErrorMsg,
  UserResponseWithUserGroup,
  WithKey,
} from '../../utils';

import { AppDispatch } from '../../store';
import { setOrderOrganization } from '../../store/order';

import { isRecurringMonthlyExtensionEnabled, useRecurringMonthlyExtensionsEnabled } from '../../utils/recurringMonthlyExtension';

interface CustomerOrganizationFormParamTypes {
  id?: string;
}

export const CustomerOrganizationForm = () => {
  const history = useHistory();
  const { t } = useTranslation('organizationForm');
  const dispatch: AppDispatch = useDispatch();

  const recurringMonthlyExtensionsEnabled = useRecurringMonthlyExtensionsEnabled();

  const organizationId = useParams<CustomerOrganizationFormParamTypes>().id || undefined;
  const addMode = !organizationId; // whether new customer org is being created or existing one updated

  const [loading, setLoading] = useState<boolean>(true);
  const [loadingPim, setLoadingPim] = useState<boolean>(false);
  // Error which prevents using this customer org form (e.g. backend down)
  const [error, setError] = useState<boolean>(false);

  const [notification, setNotification] = useState<{
    show: boolean;
    variant: GrowlNotificationSupportedVariant;
    text: string;
  }>({ show: false, variant: 'success', text: '' });

  const [allCustomerOrganizations, setAllCustomerOrganizations] = useState<OrganizationResponse[]>([]);
  const [selectedParentOrganizationId, setSelectedParentOrganizationId] = useState<string>();

  const handleParentOrganizationChange = (option: OptionListOption | undefined) => {
    if (!option) {
      setSelectedParentOrganizationId(undefined);
      return;
    }
    const selectedOrganization = allCustomerOrganizations.find(org => org.id === option!.value);
    setSelectedParentOrganizationId(selectedOrganization?.id);
  };

  const [salesChannelsInOrganizationsCountry, setSalesChannelsInOrganizationsCountry] = useState<SalesChannelTsInterface[]>([]);
  const [orderChannelsInSystem, setOrderChannelsInSystem] = useState<Channel[]>([]);

  // Company information
  const [tscId, setTscId] = useState<string | undefined>();
  const [partyId, setPartyId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [businessId, setBusinessId] = useState<string | undefined>();
  const [initialBusinessId, setInitialBusinessId] = useState<string>('');
  const [vatId, setVatId] = useState<string | undefined>();
  const [teliaFinanceNumber, setTeliaFinanceNumber] = useState<string | undefined>();
  const [technicalContactEmail, setTechnicalContactEmail] = useState<string | null>(null);

  interface ExtendedRegionOptionListOption extends OptionListOption {
    languageTag: string;
    currency: string;
  }

  const regions: ExtendedRegionOptionListOption[] = [
    { label: t('company_information.region_norway_label'), value: 'NO', languageTag: 'no-NO', currency: 'NOK' },
    { label: t('company_information.region_finland_label'), value: 'FI', languageTag: 'fi-FI', currency: 'EUR' },
    { label: t('company_information.region_sweden_label'), value: 'SE', languageTag: 'sv-SE', currency: 'SEK' },
  ];
  const [regionSelected, setRegionSelected] = useState<ExtendedRegionOptionListOption>(regions[0]);
  const regionOptions: OptionListOption[] = regions;
  const [initialRegionSelected, setInitialRegionSelected] = useState<string>('NO');

  const [orderChannelCode, setOrderChannelCode] = useState<string>();
  const [orderChannel, setOrderChannel] = useState<Channel>();

  const [finnishSorviId, setFinnishSorviId] = useState<string | null>(null);

  // Billing information
  const [billingAddress, setBillingAddress] = useState<BillingAddressDraft>();
  const [billingContactPerson, setBillingContactPerson] = useState<BillingContactPersonDraft>();

  // Device enrollment
  const [appleEnrollmentId, setAppleEnrollmentId] = useState<string | null>();
  const [samsungEnrollmentId, setSamsungEnrollmentId] = useState<string | null>();

  // Sales channel
  const [salesChannel, setSalesChannel] = useState<SalesChannelTsInterface>();
  const [salesChannelUuid, setSalesChannelUuid] = useState<string>();

  const [eolExtension, setEolExtension] = useState<boolean>(false);
  const [eolBuyout, setEolBuyout] = useState<boolean>(false);

  const [limitToOneDevice, setLimitToOneDevice] = useState<boolean>(false);

  const [companyDevicePolicy, setCompanyDevicePolicy] = useState<string | null>();

  const [customerOrgValidationErrors, setCustomerOrgValidationErrors] = useState<Errors>();

  const [administrators, setAdministrators] = useState<WithKey<UserResponseWithUserGroup>[]>([]);
  const [originalAdministrators, setOriginalAdministrators] = useState<UserResponseWithUserGroup[]>([]);
  const [userGroups, setUserGroups] = useState<WithKey<UserGroupWithUsersResponse>[]>([]);

  const [customerCreated, setCustomerCreated] = useState(false);
  const [usersCreated, setUsersCreated] = useState(false);

  const [currentOrganizationResponse, setCurrentOrganizationResponse] = useState<AxiosResponse<OrganizationResponse>>();

  const handleAdminCreate = async (administrator: UserResponseWithUserGroup) => {
    setAdministrators(previousAdministrators => [...(previousAdministrators || []), addKey(administrator)]);
  };

  const handleAdminUpdate = async (editedAdministrator: WithKey<UserResponseWithUserGroup>) => {
    const administratorIndex = administrators.findIndex(administrator => compareIds(administrator, editedAdministrator));
    setAdministrators(previousAdministrators => [
      ...previousAdministrators.slice(0, administratorIndex),
      editedAdministrator,
      ...previousAdministrators.slice(administratorIndex + 1),
    ]);
  };

  const handleGroupCreate = async (group: WithKey<UserGroupResponse>) => {
    setUserGroups(previousGroups => [...(previousGroups || []), group]);
  };

  enum Phase {
    SelectRegion = 'SELECT_REGION',
    CreateCustomer = 'CREATE_CUSTOMER',
    InviteAdministrators = 'INVITE_ADMINISTRATORS',
  }

  const [phase, setPhase] = useState<Phase | undefined>(undefined);

  const initializeExistingCustomerOrgData = async (customerOrganization: OrganizationWithUserGroupsResponse) => {
    setTscId(customerOrganization.tsc_id);
    setPartyId(customerOrganization.party_id);
    setCompanyName(customerOrganization.name);
    setBusinessId(customerOrganization.organization_number);
    setVatId(customerOrganization.vat_number);
    setTeliaFinanceNumber(customerOrganization.call_off_number);
    setTechnicalContactEmail(customerOrganization.technical_contact_email);

    setOrderChannelCode(customerOrganization.order_channel);
    setSalesChannelUuid(customerOrganization.sales_channel);
    setFinnishSorviId(customerOrganization.epicor_contract_id);

    setRegionSelected(regions.find(region => region.value === customerOrganization.locale.country) || regions[0]);

    setAppleEnrollmentId(customerOrganization.apple_enrollment_id);
    setSamsungEnrollmentId(customerOrganization.samsung_enrollment_id);

    setBillingAddress(customerOrganization.default_billing_address || undefined);
    setBillingContactPerson(customerOrganization.default_billing_contact || undefined);

    setCompanyDevicePolicy(customerOrganization.company_device_policy_url);

    setSelectedParentOrganizationId(customerOrganization.parent_organization?.id);

    setEolExtension(customerOrganization?.end_of_lease_options?.extension || false);
    setEolBuyout(customerOrganization?.end_of_lease_options?.buyout || false);

    setLimitToOneDevice(customerOrganization?.policies?.single_device_policy || false);

    setUserGroups(customerOrganization.user_groups || []);

    const adminUsers = (customerOrganization.user_groups || [])
      .map(userGroup => userGroup.users?.map(user => getUserResponseFromUserListUser(user, userGroup, customerOrganization)) || [])
      .flat()
      .filter(user => user.user_roles.effective.includes(UserRole.Admin));

    const fullAdminUsers = await Promise.all(
      adminUsers.map(async user => (await assetAdminUserClient.adminUserRead(customerOrganization.id!, user.user_group?.id!, user.id!)).data),
    );
    const usersWithUserGroups = fullAdminUsers.map(fullUser => ({
      ...fullUser,
      user_group: adminUsers.find(user => user.id === fullUser.id)?.user_group,
    }));
    setAdministrators(usersWithUserGroups);
    setOriginalAdministrators(usersWithUserGroups);
  };

  const fetchCustomerOrganizationData = async (organizationId: string) => {
    setLoading(true);
    try {
      const customerOrgResponse = await assetAdminOrganizationClient.adminOrganizationRead(organizationId, 'user_groups,user_groups.users');
      await initializeExistingCustomerOrgData(customerOrgResponse.data);
      dispatch(setOrderOrganization(customerOrgResponse.data));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      setPhase(Phase.CreateCustomer);
      fetchCustomerOrganizationData(organizationId);
    } else {
      setPhase(Phase.SelectRegion);
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (tscId) {
      setTscId(tscId.replaceAll(/\s/g, ''));
    }
  }, [tscId]);

  useEffect(() => {
    (async () => {
      setSalesChannel(undefined);
      try {
        const salesChannelsResponse = await productServiceAdminSalesChannelsClient.getSalesChannels(regionSelected?.value);
        setSalesChannelsInOrganizationsCountry(salesChannelsResponse.data.sales_channels);
      } catch {
        setError(true);
      }
    })();
  }, [regionSelected]);

  useEffect(() => {
    (async () => {
      try {
        const orderChannelsResponse = await orderAdminChannelClient.getChannelList();
        setOrderChannelsInSystem(orderChannelsResponse.data.contents);
      } catch {
        setError(true);
      }
    })();
  }, []);

  useEffect(() => {
    const salesChannel = salesChannelsInOrganizationsCountry.find(sc => sc.uuid && sc.uuid === salesChannelUuid);
    setSalesChannel(salesChannel);
  }, [salesChannelUuid, salesChannelsInOrganizationsCountry]);

  useEffect(() => {
    const orderChannel = orderChannelsInSystem.find(orderChannel => orderChannel.code === orderChannelCode);
    setOrderChannel(orderChannel);
  }, [orderChannelCode, orderChannelsInSystem]);

  useEffect(() => {
    (async () => {
      try {
        const customerOrgsResponse = await assetAdminOrganizationClient.adminOrganizationList(undefined, undefined, 9999);
        const organizationsFilteredAndSorted = customerOrgsResponse.data.contents
          .filter(org => org.id !== organizationId)
          .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));

        setAllCustomerOrganizations(organizationsFilteredAndSorted);
      } catch {
        setError(true);
      }
    })();
  }, []);

  const createBackendCustomerOrganization = (): AdminOrganizationRequest | undefined => {
    if (regionSelected) {
      const eolOptions: EndOfLeaseOptions = {
        buyout: eolBuyout,
        extension: eolExtension,
        return: true,
      };
      const policies: OrganizationPolicies = {
        single_device_policy: limitToOneDevice,
      };
      return {
        tsc_id: tscId || '',
        name: companyName,
        vat_number: vatId || '',
        organization_number: businessId || '',
        call_off_number: teliaFinanceNumber || '',

        company_device_policy_url: companyDevicePolicy || null,

        parent_organization_id: selectedParentOrganizationId,

        locale: {
          country: regionSelected.value,
        },
        epicor_contract_id: regionSelected.value === 'FI' ? finnishSorviId : null,
        technical_contact_email: technicalContactEmail,

        apple_enrollment_id: appleEnrollmentId || '',
        samsung_enrollment_id: samsungEnrollmentId || '',

        sales_channel: salesChannel?.uuid || '',

        default_billing_address: billingAddress as Address,
        default_billing_contact: billingContactPerson as Person,

        end_of_lease_options: eolOptions,
        policies: policies,
        default_delivery_address: null,
        default_delivery_contact: null,
      };
    }
  };

  const fetchOrganizationInfoFromPim = async (countryCode: string, initialBusinessId: string) => {
    const externalOrganization = (await assetAdminOrganizationClient.adminExternalOrganization(countryCode, initialBusinessId)).data;
    setCompanyName(externalOrganization.name);
    setBusinessId(externalOrganization.organization_number);
    setBillingAddress(externalOrganization.address ?? undefined);
    setPartyId(externalOrganization.party_id);
    if (externalOrganization.organization_id_in_ukko) {
      throw new Error('Organization already exists');
    } else {
      setCustomerOrgValidationErrors(undefined);
    }
  };

  const handleNextInRegionSelectionPhase = async () => {
    if (initialRegionSelected === 'NO') {
      try {
        setLoadingPim(true);
        await fetchOrganizationInfoFromPim('NO', initialBusinessId!);
        setLoadingPim(false);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.data?.status === NotFoundErrorStatusEnum.NUMBER_404) {
          const error = setCustomerBusinessIdNorwayError(t);
          setCustomerOrgValidationErrors(error);
        } else if (err instanceof Error && err.message === 'Organization already exists') {
          setCustomerOrgValidationErrors(setCustomerBusinessIdAlreadyExists(t));
        } else {
          setNotification({ show: true, text: t('notifications.error_pim_organization_no'), variant: 'error' });
        }

        setLoadingPim(false);
        return;
      }
    } else {
      setCompanyName('');
      setBusinessId(initialBusinessId);
      setPartyId(null);
      setBillingAddress(undefined);
    }

    setRegionSelected(regions.find(region => region.value === initialRegionSelected)!);
    setPhase(Phase.CreateCustomer);
  };

  const handleNextInCreateCustomerPhase = () => {
    const customerOrg = createBackendCustomerOrganization();
    if (!customerOrg) return;
    const { valid, errors } = validateCustomerOrganization(customerOrg, salesChannel, regionSelected, t);
    if (!valid) {
      setCustomerOrgValidationErrors(errors);
      setNotification({ show: true, text: t('notifications.error_validation'), variant: 'error' });
    } else {
      setCustomerOrgValidationErrors(undefined);
      setPhase(Phase.InviteAdministrators);
      setNotification({ show: false, variant: 'success', text: '' });
    }
  };

  const handleSubmit = async () => {
    const backendCustomerOrg: AdminOrganizationRequest | undefined = createBackendCustomerOrganization();
    if (!backendCustomerOrg) return;
    const { valid, errors } = validateCustomerOrganization(backendCustomerOrg, salesChannel, regionSelected, t);
    if (!valid) {
      setCustomerOrgValidationErrors(errors);
      setNotification({ show: true, text: t('notifications.error_validation'), variant: 'error' });
      return;
    }
    setCustomerOrgValidationErrors(undefined);

    setLoading(true);
    try {
      let successNotificationText = '';
      const upsertUsers = async (organizationResponse?: AxiosResponse<OrganizationWithUserGroupsResponse>) => {
        if (organizationResponse?.data.id) {
          const createdUserGroups = await Promise.all(
            userGroups.map(async group =>
              !group.id
                ? {
                    ...group, // preserve keys
                    ...(
                      await assetAdminUserGroupClient.adminUserGroupCreate(
                        organizationResponse.data.id!,
                        getUserGroupRequestToCreateNewUserGroup(group),
                      )
                    ).data,
                  }
                : group,
            ),
          );
          setUserGroups(createdUserGroups);
          const updatedAdministrators = await Promise.all(
            administrators.map(async administrator => {
              const userGroup = createdUserGroups.find(userGroup => compareIds(userGroup, administrator.user_group));
              if (userGroup?.id) {
                if (administrator.id) {
                  const originalAdministrator = originalAdministrators.find(originalAdministrator => originalAdministrator.id === administrator.id);
                  if (!isEqual(administrator, originalAdministrator)) {
                    const userUpdateRequest = getUserUpdateRequest(administrator);
                    if (userUpdateRequest) {
                      return (
                        await assetAdminUserClient.adminUserUpdate(organizationResponse.data.id!, userGroup.id, administrator.id, userUpdateRequest)
                      ).data;
                    }
                  }
                } else {
                  const userRequest = getUserRequest(administrator);
                  if (userRequest) {
                    return (await assetAdminUserClient.adminUserCreate(organizationResponse.data.id!, userGroup.id, userRequest)).data;
                  }
                }
              }
              return administrator;
            }),
          );
          setAdministrators(updatedAdministrators);
          setOriginalAdministrators(updatedAdministrators);
        }
      };
      let organizationResponse: AxiosResponse<OrganizationResponse> | undefined;
      const adminOrganizationRequest: AdminOrganizationRequest = { ...backendCustomerOrg };
      if (addMode) {
        if (!customerCreated) {
          organizationResponse = await assetAdminOrganizationClient.adminOrganizationCreate(adminOrganizationRequest);
          setCurrentOrganizationResponse(organizationResponse);
        } else if (currentOrganizationResponse) {
          organizationResponse = currentOrganizationResponse;
        }
        setCustomerCreated(true);
        await upsertUsers(organizationResponse);
        successNotificationText = t('notifications.successful_create');
        setUsersCreated(true);
      } else if (organizationId) {
        organizationResponse = await assetAdminOrganizationClient.adminOrganizationUpdate(organizationId, adminOrganizationRequest);
        setCurrentOrganizationResponse(organizationResponse);
        await upsertUsers(organizationResponse);
        successNotificationText = t('notifications.successful_update');
      }
      setNotification({ show: true, text: successNotificationText, variant: 'success' });
      if (!addMode) {
        fetchCustomerOrganizationData(organizationId);
      }
    } catch (err) {
      const defaultErrorMsg = addMode ? t('notifications.error_create') : t('notifications.error_update');
      const axiosErrorResponse = axios.isAxiosError(err) ? err.response : undefined;
      setNotification({
        show: true,
        text: mapAssetBackendErrorCodeToLocalizedErrorMsg(
          axiosErrorResponse?.data,
          defaultErrorMsg,
          allCustomerOrganizations.find(org => org.id === selectedParentOrganizationId)?.name,
          companyName,
        ),
        variant: 'error',
      });
      if (axiosErrorResponse?.data?.error === BadRequestErrorErrorEnum.BadRequest) {
        setCustomerOrgValidationErrors(setParentOrganizationError(t));
      }
    } finally {
      setLoading(false);
    }
  };

  const OnboardingStatus = () => {
    if (loading) {
      return <></>;
    }

    const allActivated = administrators.every(admin => {
      // After admin user has logged in for the first time (=is activated), they should have a first and last name
      return admin.given_name && admin.family_name;
    });

    if (administrators.length > 0 && allActivated) {
      return (
        <Flex centeredVertically>
          <Tag backgroundColor="blue600">{t('onboarding.complete')}</Tag>
        </Flex>
      );
    }

    const infoMessage =
      administrators.length === 0 ? t('onboarding.incomplete_info_no_administrators') : t('onboarding.incomplete_info_not_activated');

    return (
      <Flex directionColumn>
        <Flex>
          <Tag backgroundColor="orange400">{t('onboarding.incomplete')}</Tag>
        </Flex>
        <Gutter size="layout-4" />
        <BodyText size="sm">{infoMessage}</BodyText>
      </Flex>
    );
  };

  const render = () => {
    let content;

    const phaseContents = {
      [Phase.SelectRegion]: (
        <RegionSelection
          loadingPim={loadingPim}
          regions={regions}
          initialRegionSelected={initialRegionSelected}
          setInitialRegionSelected={setInitialRegionSelected}
          initialBusinessId={initialBusinessId}
          setInitialBusinessId={setInitialBusinessId}
          customerBusinessIdForNorwayError={customerOrgValidationErrors?.customerBusinessIdForNorway}
          handleNextInRegionSelectionPhase={handleNextInRegionSelectionPhase}
          setCustomerOrgValidationErrors={setCustomerOrgValidationErrors}
        />
      ),
      [Phase.CreateCustomer]: (
        <Div>
          {!addMode && (
            <>
              <Flex spaceBetween centeredVertically>
                <OnboardingStatus />
                <Flex centeredVertically alignRight>
                  <Button variant="secondary" onClick={() => history.push(`/`)}>
                    {t('cancel_btn')}
                  </Button>
                  <Gutter size="sm" />
                  <Button disabled={false} variant="primary" onClick={() => handleSubmit()}>
                    {t('save_btn')}
                  </Button>
                </Flex>
              </Flex>
              <Gutter size="layout-4" />
            </>
          )}
          <CompanyInformation
            addMode={addMode}
            tscid={tscId}
            partyId={partyId}
            setTscId={setTscId}
            companyName={companyName}
            setCompanyName={setCompanyName}
            businessId={businessId}
            setBusinessId={setBusinessId}
            vatId={vatId}
            setVatId={setVatId}
            teliaFinanceNumber={teliaFinanceNumber}
            setTeliaFinanceNumber={setTeliaFinanceNumber}
            technicalContactEmail={technicalContactEmail}
            setTechnicalContactEmail={setTechnicalContactEmail}
            regionOptions={regionOptions}
            regionSelected={regionSelected}
            setRegionSelected={(selected: OptionListOption) => {
              const newRegionSelected = regions.find(region => region.value === selected.value);
              if (newRegionSelected && newRegionSelected.value !== regionSelected.value) {
                setRegionSelected(newRegionSelected);
                setSalesChannelUuid(undefined);
              }
            }}
            orderChannel={orderChannel}
            finnishSorviId={finnishSorviId}
            setFinnishSorviId={setFinnishSorviId}
            errors={customerOrgValidationErrors?.companyInformation}
          />
          <Gutter size="layout-5" />
          <ParentOrganization
            selectedParentOrganizationId={selectedParentOrganizationId}
            handleParentOrganizationChange={handleParentOrganizationChange}
            organizationList={allCustomerOrganizations}
            editingOrganizationName={companyName}
            error={customerOrgValidationErrors?.parentOrganization}
          />
          <Gutter size="layout-5" />
          <BillingInformation
            billingAddress={billingAddress}
            setBillingAddress={setBillingAddress}
            billingContactPerson={billingContactPerson}
            setBillingContactPerson={setBillingContactPerson}
            addressErrors={customerOrgValidationErrors?.billingAddress}
            contactPersonErrors={customerOrgValidationErrors?.billingContact}
          />
          <Gutter size="layout-5" />
          {!addMode && (
            <>
              <Administrators
                administrators={administrators}
                userGroups={userGroups}
                currency={regionSelected.currency}
                onAdminCreate={handleAdminCreate}
                onAdminUpdate={handleAdminUpdate}
                onGroupCreate={handleGroupCreate}
              />
              <Gutter size="layout-5" />
            </>
          )}
          <DeviceEnrollment
            appleEnrollmentId={appleEnrollmentId}
            setAppleEnrollmentId={setAppleEnrollmentId}
            samsungEnrollmentId={samsungEnrollmentId}
            setSamsungEnrollmentId={setSamsungEnrollmentId}
          />
          <Gutter size="layout-5" />
          <SalesChannel
            salesChannel={salesChannel}
            setSalesChannelUuid={setSalesChannelUuid}
            salesChannelsInOrganizationsCountry={salesChannelsInOrganizationsCountry}
            errors={customerOrgValidationErrors?.salesChannel}
          />
          <Gutter size="layout-5" />
          <EndOfLease
            eolExtension={eolExtension}
            setEolExtension={setEolExtension}
            eolBuyout={eolBuyout}
            setEolBuyout={setEolBuyout}
            showExtensionOption={!isRecurringMonthlyExtensionEnabled(regionSelected.value, recurringMonthlyExtensionsEnabled)}
          />
          <Gutter size="layout-5" />
          <DeviceLimit limitToOneDevice={limitToOneDevice} setLimitToOneDevice={setLimitToOneDevice} />
          <Gutter size="layout-5" />
          <CompanyDevicePolicy
            companyDevicePolicy={companyDevicePolicy}
            setCompanyDevicePolicy={setCompanyDevicePolicy}
            validationError={customerOrgValidationErrors?.companyDevicePolicyLink}
          />
          <Gutter size="layout-5" />
          {addMode && (
            <>
              {/* Save whole form at once or cancel */}
              <Flex>
                <Button variant="primary" onClick={handleNextInCreateCustomerPhase}>
                  {t('nextButton')}
                </Button>
                <Gutter size="sm" />
                <Button variant="secondary" onClick={() => setPhase(Phase.SelectRegion)}>
                  {t('goBackButton')}
                </Button>
              </Flex>
              <Gutter size="layout-5" />
            </>
          )}
        </Div>
      ),
      [Phase.InviteAdministrators]: (
        <>
          <Administrators
            administrators={administrators}
            userGroups={userGroups}
            currency={regionSelected.currency}
            onAdminCreate={handleAdminCreate}
            onAdminUpdate={handleAdminUpdate}
            onGroupCreate={handleGroupCreate}
          />
          <Gutter size="layout-5" />
          {/* Save whole form at once or cancel */}
          <Flex>
            <Button variant="primary" onClick={handleSubmit}>
              {t('save_and_create_service_btn')}
            </Button>
            <Gutter size="sm" />
            <Button variant="secondary" onClick={() => setPhase(Phase.CreateCustomer)}>
              {t('goBackButton')}
            </Button>
          </Flex>
          <Gutter size="layout-5" />
        </>
      ),
    };

    if (loading) {
      content = <Loader />;
    } else if (error) {
      content = <Message text={t('errors.initial_fetch_error')} />;
    } else if (customerCreated && usersCreated) {
      content = (
        <>
          <Gutter />
          <Title size={'lg'}>{t('customerCreated.heading')}</Title>
          <Gutter />
          <BodyText>{t('customerCreated.createdText')}</BodyText>
          <Gutter />
          <BodyText>{t('customerCreated.editText')}</BodyText>
          <Gutter />
          <Button variant="primary" onClick={() => history.push('/')}>
            {t('customerCreated.backToFrontPageButton')}
          </Button>
        </>
      );
    } else {
      const setPhaseContent = () => {
        if (addMode && phase === Phase.SelectRegion) {
          return phaseContents.SELECT_REGION;
        } else if (addMode && phase === Phase.InviteAdministrators) {
          return phaseContents.INVITE_ADMINISTRATORS;
        } else {
          return phaseContents.CREATE_CUSTOMER;
        }
      };
      content = (
        <>
          {addMode && (
            <>
              <Gutter size="layout-5" />
              <Title size={'lg'}>{t('create_customer_organization')}</Title>
              <Gutter size={'layout-5'} />
              <Steps selectedStepIndex={Object.entries(Phase).findIndex(([, currentPhase]) => currentPhase === phase)}>
                <Step label="1" description={t('stepper.selectRegion')} onSelect={() => setPhase(Phase.SelectRegion)} />
                <Step label="2" description={t('stepper.createCustomer')} onSelect={() => setPhase(Phase.CreateCustomer)} />
                <Step label="3" description={t('stepper.inviteAdministrators')} />
              </Steps>
              <Gutter size="layout-7" />
            </>
          )}

          {setPhaseContent()}
        </>
      );
    }

    const growlNotification = notification.show && (
      <GrowlNotification
        notification={notification.text}
        variant={notification.variant}
        closeOnClick={() => setNotification({ show: false, text: '', variant: 'success' })}
      />
    );

    return (
      <>
        {growlNotification}
        {content}
      </>
    );
  };

  return render();
};

export default CustomerOrganizationForm;
